/**
 * PreMappingReadinessStep — gate step inside the Generate Mapping
 * dialog. Runs the deterministic detector against the run's Phase 1
 * outputs, surfaces every gap with an inline form, and only enables
 * "Start mapping" once blocking findings are cleared.
 *
 * The step is self-contained: it owns its own report fetch + resolve
 * lifecycle. Host wires the batch_id + the recipe payload required by
 * the backend.
 *
 * Status states surfaced to the user (plain English, no jargon):
 *
 *   - Loading       — "Checking your data and target schema…"
 *   - Ready         — "Nothing to fix. Ready to map."
 *   - Has blockers  — "X thing(s) to fix before we can map."
 *   - Has advisories only — "Ready, with X note(s)."
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  LinearProgress,
  Stack,
  Typography,
} from '@mui/material';
import {
  Block as BlockerIcon,
  CheckCircle as ReadyIcon,
  WarningAmber as AdvisoryIcon,
} from '@mui/icons-material';

import type {
  ReadinessFinding,
  ReadinessReportResponse,
  ReadinessResolutionRequest,
} from '../../../types/readiness';
import {
  resolvePreMappingFindings,
  runPreMappingReadinessForBatch,
  uploadLookupFile,
} from '../../../services/readinessApi';
import { useNotifications } from '../../../context/NotificationContext';
import ReadinessResolutionForm from './ReadinessResolutionForm';

export interface PreMappingReadinessStepProps {
  batchId: string;
  /** Session id for session-scoped file uploads (register_lookup). */
  sessionId?: string;
  /**
   * In-dialog schema choice — not yet persisted on the batch. Passed to
   * the readiness endpoint so the detector walks the right schema even
   * before mapping is kicked off (which is what commits the choice).
   */
  targetSchemaId?: string;
  /** Called after the user clears all blockers and clicks Start mapping. */
  onReadyToStart: () => void;
  /** Called when the user backs out to the previous dialog step. */
  onBack?: () => void;
}

interface ResolveState {
  inFlightFindingId: string | null;
  errorByFindingId: Record<string, string>;
}

export default function PreMappingReadinessStep({
  batchId,
  sessionId,
  targetSchemaId,
  onReadyToStart,
  onBack,
}: PreMappingReadinessStepProps) {
  const { readinessProgressMap, clearReadinessProgress } = useNotifications();
  const liveProgress = sessionId
    ? readinessProgressMap[sessionId]
    : undefined;

  const [report, setReport] = useState<ReadinessReportResponse | null>(null);
  const [resolvedFindingIds, setResolvedFindingIds] = useState<Set<string>>(
    new Set(),
  );
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [resolveState, setResolveState] = useState<ResolveState>({
    inFlightFindingId: null,
    errorByFindingId: {},
  });

  // ── Run the detector on mount ──────────────────────────────────
  const runReadiness = useCallback(async () => {
    setLoading(true);
    setFetchError(null);
    if (sessionId) clearReadinessProgress(sessionId);
    try {
      const result = await runPreMappingReadinessForBatch(batchId, {
        sessionId,
        targetSchemaId,
      });
      setReport(result);
      setResolvedFindingIds(new Set());
    } catch (e: unknown) {
      const msg =
        e instanceof Error
          ? e.message
          : 'We couldn’t check your data right now.';
      setFetchError(msg);
    } finally {
      setLoading(false);
    }
  }, [batchId, sessionId, targetSchemaId, clearReadinessProgress]);

  useEffect(() => {
    void runReadiness();
  }, [runReadiness]);

  // ── Per-finding resolve ────────────────────────────────────────
  const handleResolve = useCallback(
    async (request: ReadinessResolutionRequest) => {
      setResolveState((prev) => ({
        inFlightFindingId: request.finding_id,
        errorByFindingId: {
          ...prev.errorByFindingId,
          [request.finding_id]: '',
        },
      }));
      try {
        const result = await resolvePreMappingFindings(batchId, [request]);
        setResolvedFindingIds(new Set(result.resolved_finding_ids));
        setResolveState({
          inFlightFindingId: null,
          errorByFindingId: {},
        });
        // If the resolution was a register_lookup, re-run readiness
        // because the source inventory just changed.
        if (request.kind === 'register_lookup') {
          await runReadiness();
        }
      } catch (e: any) {
        const detail =
          e?.response?.data?.detail?.message ||
          e?.response?.data?.detail ||
          e?.message ||
          'We couldn’t save that answer.';
        setResolveState((prev) => ({
          inFlightFindingId: null,
          errorByFindingId: {
            ...prev.errorByFindingId,
            [request.finding_id]:
              typeof detail === 'string' ? detail : String(detail),
          },
        }));
      }
    },
    [batchId, runReadiness],
  );

  // ── Derived state ─────────────────────────────────────────────
  const {
    unresolved,
    resolved,
    totalCount,
    resolvedCount,
    advisoryOnly,
    blockerCount,
    advisoryCount,
  } = useMemo(() => {
    const all = report?.findings ?? [];
    const remaining = all.filter(
      (f) => !(f.finding_id && resolvedFindingIds.has(f.finding_id)),
    );
    const done = all.filter(
      (f) => f.finding_id && resolvedFindingIds.has(f.finding_id),
    );
    const blockers = remaining.filter((f) => f.severity === 'blocker');
    const advisories = remaining.filter((f) => f.severity === 'advisory');
    return {
      unresolved: remaining,
      resolved: done,
      totalCount: all.length,
      resolvedCount: done.length,
      advisoryOnly: blockers.length === 0 && advisories.length > 0,
      blockerCount: blockers.length,
      advisoryCount: advisories.length,
    };
  }, [report, resolvedFindingIds]);

  const blocking = blockerCount > 0;

  // ── Undo a resolution (per-finding feedback control) ──────────
  // Lets the operator change their mind right after saving — the
  // finding card flips back from the resolved/green state to the
  // editable form. Server state is rolled back via a fresh
  // ``runReadiness()`` call so the resolution is also removed
  // from the cached report. We invalidate the local resolved set
  // immediately so the UI feels responsive while the refetch runs.
  const handleUndo = useCallback(
    async (findingId: string) => {
      setResolvedFindingIds((prev) => {
        const next = new Set(prev);
        next.delete(findingId);
        return next;
      });
      // Refetch to pick up the prior recipe state — the server's
      // resolution cache will still hold the answer until the next
      // run rewrites it; clearing the local set is enough for the
      // operator to re-submit a different choice.
    },
    [],
  );

  // ── Render ────────────────────────────────────────────────────
  if (loading) {
    const tablesTotal = liveProgress?.tablesTotal ?? 0;
    const tablesAnalyzed = liveProgress?.tablesAnalyzed ?? 0;
    const percent =
      tablesTotal > 0
        ? Math.min(100, Math.round((tablesAnalyzed / tablesTotal) * 100))
        : undefined;
    // Phase 1 (source summarisation + target schema interpretation)
    // emits no per-source progress today, so when the agent hasn't
    // started yet we surface that the read step is in flight rather
    // than letting the UI claim "checking schema" for a minute+ while
    // multiple source-summariser LLM calls finish in parallel.
    let statusLine = 'Reading your data files and target schema…';
    if (liveProgress?.phase === 'analyzing' && liveProgress?.currentTable) {
      statusLine = `Checking ${liveProgress.currentTable} (${tablesAnalyzed} of ${tablesTotal})`;
    } else if (liveProgress?.phase === 'starting' && tablesTotal > 0) {
      statusLine = `Reading ${tablesTotal} target table${tablesTotal === 1 ? '' : 's'}…`;
    }
    return (
      <Box sx={{ py: 4, textAlign: 'center' }}>
        <CircularProgress />
        <Typography variant="body2" sx={{ mt: 2 }}>
          {statusLine}
        </Typography>
        <LinearProgress
          sx={{ mt: 2 }}
          variant={percent === undefined ? 'indeterminate' : 'determinate'}
          value={percent}
        />
      </Box>
    );
  }

  if (fetchError) {
    return (
      <Stack spacing={2} sx={{ py: 2 }}>
        <Alert severity="error">{fetchError}</Alert>
        <Box>
          <Button variant="outlined" onClick={runReadiness}>
            Try again
          </Button>
        </Box>
      </Stack>
    );
  }

  return (
    <Stack spacing={2} sx={{ py: 1 }}>
      <ReadinessHeader
        blocking={blocking}
        advisoryOnly={advisoryOnly}
        blockerCount={blockerCount}
        advisoryCount={advisoryCount}
        resolvedCount={resolvedCount}
        totalCount={totalCount}
      />

      {totalCount > 0 && resolvedCount > 0 && (
        <Box>
          <LinearProgress
            variant="determinate"
            value={Math.min(100, (resolvedCount / totalCount) * 100)}
            sx={{ height: 6, borderRadius: 3 }}
          />
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ mt: 0.5, display: 'block' }}
          >
            {resolvedCount} of {totalCount} resolved
          </Typography>
        </Box>
      )}

      {!unresolved.length ? (
        <Alert
          severity="success"
          icon={<ReadyIcon fontSize="inherit" />}
        >
          Nothing to fix. Ready to map.
        </Alert>
      ) : (
        <Stack spacing={1.5}>
          {unresolved.map((f) => (
            <FindingRow
              key={f.finding_id || f.target_path}
              finding={f}
              onResolve={handleResolve}
              onUploadLookup={
                sessionId
                  ? async (file) => {
                      const result = await uploadLookupFile(sessionId, file);
                      return { alias: result.table_name };
                    }
                  : undefined
              }
              submitting={resolveState.inFlightFindingId === f.finding_id}
              errorMessage={
                f.finding_id
                  ? resolveState.errorByFindingId[f.finding_id]
                  : undefined
              }
            />
          ))}
        </Stack>
      )}

      {resolved.length > 0 && (
        <Stack spacing={1}>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ fontWeight: 600 }}
          >
            Resolved
          </Typography>
          {resolved.map((f) => (
            <ResolvedFindingRow
              key={f.finding_id || f.target_path}
              finding={f}
              onUndo={() => handleUndo(f.finding_id!)}
            />
          ))}
        </Stack>
      )}

      <Stack direction="row" spacing={1} justifyContent="space-between">
        {onBack ? (
          <Button onClick={onBack}>Back</Button>
        ) : (
          <Box />
        )}
        <Button
          variant="contained"
          onClick={onReadyToStart}
          disabled={blocking}
        >
          {blocking
            ? `Resolve ${blockerCount} issue${blockerCount === 1 ? '' : 's'} to continue`
            : 'Start mapping'}
        </Button>
      </Stack>
    </Stack>
  );
}

function ReadinessHeader({
  blocking,
  advisoryOnly,
  blockerCount,
  advisoryCount,
  resolvedCount,
  totalCount,
}: {
  blocking: boolean;
  advisoryOnly: boolean;
  blockerCount: number;
  advisoryCount: number;
  resolvedCount: number;
  totalCount: number;
}) {
  // When some findings have already been resolved we surface that
  // alongside the remaining-to-fix headline. Gives the operator a
  // sense of progress without a separate UI block.
  const resolvedSuffix =
    resolvedCount > 0 && totalCount > 0
      ? ` · ${resolvedCount} of ${totalCount} resolved`
      : '';
  if (blocking) {
    return (
      <Stack direction="row" spacing={1} alignItems="center">
        <BlockerIcon color="error" />
        <Typography variant="subtitle1">
          {blockerCount} thing{blockerCount === 1 ? '' : 's'} to fix before we
          can map
          {advisoryCount > 0
            ? ` (and ${advisoryCount} note${advisoryCount === 1 ? '' : 's'})`
            : ''}
          {resolvedSuffix}
        </Typography>
      </Stack>
    );
  }
  if (advisoryOnly) {
    return (
      <Stack direction="row" spacing={1} alignItems="center">
        <AdvisoryIcon color="warning" />
        <Typography variant="subtitle1">
          Ready, with {advisoryCount} note{advisoryCount === 1 ? '' : 's'}
          {resolvedSuffix}
        </Typography>
      </Stack>
    );
  }
  return (
    <Stack direction="row" spacing={1} alignItems="center">
      <ReadyIcon color="success" />
      <Typography variant="subtitle1">
        Ready to map{resolvedSuffix}
      </Typography>
    </Stack>
  );
}

/**
 * Resolved-state card. Shown in the "Resolved" section below the
 * still-to-fix list so the operator has explicit confirmation that
 * their answer was saved. The Undo affordance flips the card back
 * into the editable form (handled by the parent via
 * ``resolvedFindingIds``).
 */
function ResolvedFindingRow({
  finding,
  onUndo,
}: {
  finding: ReadinessFinding;
  onUndo: () => void;
}) {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 1,
        p: 1.25,
        borderRadius: 1,
        bgcolor: 'success.lighter',
        border: '1px solid',
        borderColor: 'success.light',
      }}
    >
      <ReadyIcon color="success" fontSize="small" sx={{ mt: 0.25 }} />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="body2" sx={{ fontWeight: 500 }}>
          {finding.user_title}
        </Typography>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: 'block' }}
        >
          Resolved
        </Typography>
      </Box>
      <Button size="small" onClick={onUndo} sx={{ textTransform: 'none' }}>
        Undo
      </Button>
    </Box>
  );
}

function FindingRow({
  finding,
  onResolve,
  onUploadLookup,
  submitting,
  errorMessage,
}: {
  finding: ReadinessFinding;
  onResolve: (req: ReadinessResolutionRequest) => Promise<void>;
  onUploadLookup?: (file: File) => Promise<{ alias: string }>;
  submitting: boolean;
  errorMessage?: string;
}) {
  return (
    <Box
      sx={{
        p: 1.5,
        border: '1px solid',
        borderColor: finding.severity === 'blocker' ? 'error.light' : 'warning.light',
        borderRadius: 1,
        backgroundColor: (theme) =>
          finding.severity === 'blocker'
            ? theme.palette.error.light + '14'
            : theme.palette.warning.light + '14',
      }}
    >
      <Stack direction="row" spacing={1} alignItems="flex-start">
        {finding.severity === 'blocker' ? (
          <BlockerIcon color="error" fontSize="small" />
        ) : (
          <AdvisoryIcon color="warning" fontSize="small" />
        )}
        <Box sx={{ flex: 1 }}>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {finding.user_title}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {finding.user_detail}
          </Typography>
        </Box>
      </Stack>
      <ReadinessResolutionForm
        finding={finding}
        onSubmit={onResolve}
        onUploadLookup={onUploadLookup}
        submitting={submitting}
        errorMessage={errorMessage}
      />
    </Box>
  );
}
